import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import './Forms.css';

function ViewForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchForm = async () => {
      if (!id) {
        console.error('No form ID provided');
        setError('No form ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('========== ViewForm Data Loading Debugging ==========');
        console.log('1. Fetching form with ID:', id);
        
        // First try the forms collection (active forms)
        const activeDoc = await getDoc(doc(db, 'forms', id));
        
        if (activeDoc.exists()) {
          const data = activeDoc.data();
          console.log('2. Retrieved form data:', data);
          console.log('3. Form styling:', {
            templateStyles: data.templateStyles,
            fontSizes: data.fontSizes,
            customStyles: data.customStyles
          });
          console.log('4. Form structure:', {
            formFields: data.formFields,
            items: data.items
          });
          console.log('Form found in active forms:', data);
          
          // Initialize form data with existing values if any
          const initialFormData = {};
          data.formFields?.forEach(field => {
            initialFormData[field.id] = field.value || '';
          });
          
          setForm({ id: activeDoc.id, ...data });
          setFormData(initialFormData);
        } else {
          // Try completed forms collection
          const completedDoc = await getDoc(doc(db, 'completed_forms', id));
          
          if (completedDoc.exists()) {
            const data = completedDoc.data();
            console.log('2. Retrieved form data:', data);
            console.log('3. Form styling:', {
              templateStyles: data.templateStyles,
              fontSizes: data.fontSizes,
              customStyles: data.customStyles
            });
            console.log('4. Form structure:', {
              formFields: data.formFields,
              items: data.items
            });
            console.log('Form found in completed forms:', data);
            setForm({ id: completedDoc.id, ...data });
            setFormData(data.formData || {});
          } else {
            console.error('5. Form not found:', id);
            setError('Form not found');
          }
        }
      } catch (error) {
        console.error('6. Error fetching form:', error);
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        setError('Error loading form');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [id]);

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));

    // Update the form fields with the new value
    setForm(prevForm => ({
      ...prevForm,
      formFields: prevForm.formFields.map(field =>
        field.id === fieldId ? { ...field, value } : field
      )
    }));
  };

  const handleItemCompletion = (itemId, completed) => {
    setForm(prevForm => ({
      ...prevForm,
      items: prevForm.items.map(item =>
        item.id === itemId ? { ...item, completed } : item
      )
    }));

    // Update the form in the database
    const formRef = doc(db, 'forms', id);
    updateDoc(formRef, {
      items: form.items.map(item =>
        item.id === itemId ? { ...item, completed } : item
      ),
      lastModified: serverTimestamp()
    }).catch(error => {
      console.error('Error updating item completion:', error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update the form in the database
      const formRef = doc(db, 'forms', id);
      await updateDoc(formRef, {
        formFields: form.formFields,
        lastModified: serverTimestamp()
      });

      navigate('/forms/active', { 
        state: { 
          message: 'Form updated successfully',
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Error updating form:', error);
      setError('Error updating form');
    } finally {
      setLoading(false);
    }
  };

  const renderFormItems = () => {
    if (!form.items || form.items.length === 0) {
      return <div className="no-items">No form items found</div>;
    }

    return form.items.map((item, index) => {
      // Get template styles for this item type
      const typeStyles = form.templateStyles?.[item.type] || {};
      
      // Get border color based on item type
      const getBorderColor = (type) => {
        switch(type) {
          case 'section':
            return 'rgba(33, 150, 243, 0.3)';
          case 'subsection':
            return 'rgba(76, 175, 80, 0.3)';
          case 'item':
            return 'rgba(255, 152, 0, 0.3)';
          default:
            return 'rgba(255, 255, 255, 0.1)';
        }
      };

      // Combine styles in the correct order of precedence
      const itemStyle = {
        // Base styles
        padding: '8px',
        margin: '4px 0',
        borderRadius: '4px',
        borderBottom: `1px solid ${getBorderColor(item.type)}`,
        transition: 'border-color 0.2s',
        marginLeft: item.type === 'section' ? '0' : 
                   item.type === 'subsection' ? '20px' : 
                   item.type === 'item' ? '40px' : '0',
        
        // Template styles (excluding background color)
        ...Object.fromEntries(
          Object.entries(typeStyles).filter(([key]) => key !== 'backgroundColor')
        ),
        
        // Font size (from item or template or default)
        fontSize: item.fontSize || 
                 form.fontSizes?.[item.type] || 
                 typeStyles.fontSize || 
                 (item.type === 'section' ? '18px' : 
                  item.type === 'subsection' ? '16px' : '14px')
      };

      const handleRowClick = (e) => {
        // Only toggle if the click wasn't directly on the checkbox
        if (e.target.type !== 'checkbox') {
          handleItemCompletion(item.id, !item.completed);
        }
      };

      if (item.type === 'section') {
        return (
          <div 
            key={item.id} 
            className={`form-section ${item.completed ? 'completed' : ''}`}
            style={itemStyle}
            onClick={handleRowClick}
          >
            <div className="form-field-content">
              <input
                type="checkbox"
                checked={item.completed || false}
                onChange={(e) => handleItemCompletion(item.id, e.target.checked)}
                className="form-field-checkbox"
              />
              <h3>{item.content}</h3>
            </div>
          </div>
        );
      }

      if (item.type === 'subsection') {
        return (
          <div 
            key={item.id} 
            className={`form-subsection ${item.completed ? 'completed' : ''}`}
            style={itemStyle}
            onClick={handleRowClick}
          >
            <div className="form-field-content">
              <input
                type="checkbox"
                checked={item.completed || false}
                onChange={(e) => handleItemCompletion(item.id, e.target.checked)}
                className="form-field-checkbox"
              />
              <h4>{item.content}</h4>
            </div>
          </div>
        );
      }

      return (
        <div 
          key={item.id}
          className={`form-field ${item.completed ? 'completed' : ''}`}
          style={itemStyle}
          onClick={handleRowClick}
        >
          <div className="form-field-content">
            <input
              type="checkbox"
              checked={item.completed || false}
              onChange={(e) => handleItemCompletion(item.id, e.target.checked)}
              className="form-field-checkbox"
            />
            <span>{item.content}</span>
          </div>
        </div>
      );
    });
  };

  if (loading) {
    return <div className="loading">Loading form...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!form) {
    return <div className="error">Form not found</div>;
  }

  // Apply template styles if they exist
  const containerStyle = {
    ...(form.templateStyles?.container || {}),
    padding: '20px'
  };

  const headerStyle = {
    ...(form.templateStyles?.header || {}),
    marginBottom: '20px'
  };

  return (
    <div className="view-form-container" style={containerStyle}>
      <div className="view-form-header" style={headerStyle}>
        <h2 style={form.templateStyles?.title || {}}>{form.name}</h2>
        {form.description && (
          <p className="form-description" style={form.templateStyles?.description || {}}>
            {form.description}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="view-form">
        {renderFormItems()}

        <div className="form-footer" style={form.templateStyles?.footer || {}}>
          <div className="button-group">
            <button 
              type="button" 
              onClick={() => navigate('/forms/active')}
              className="cancel-button"
              style={form.templateStyles?.button || {}}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
              style={{
                ...form.templateStyles?.button,
                ...form.templateStyles?.submitButton
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ViewForm;
